import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  BtnSize,
  BtnStyle,
  Button,
  Header,
  Modal,
  ModalSize,
  Sidebar,
  ToastHolder,
} from "./components";
import { BiskoScript } from "./components/analytics/bisko/BiskoScript";
import {
  OrganizationSettingsProvider,
  TfaProvider,
  UserProfileProvider,
} from "./context";
import { useFetch, usePreviewMode, useToggle } from "./hooks";
import { Router as MainRouter } from "./router/Router";
import {
  Compose,
  GIFTS_PATH,
  PAYMENTS_PATH,
  RECOVERY_EMAIL_PATH,
  SUBSCRIPTIONS_PATH,
  cx,
  getUserInfo,
  primaryBtnClasses,
  templatesApiUrl,
} from "./utils";

const App = () => {
  const { t } = useTranslation();
  const { visible, setVisible } = useToggle();
  const { access_token } = getUserInfo();

  const { pathname } = useLocation();
  const { isPreviewMode } = usePreviewMode();
  const navigate = useNavigate();
  const isPrimaryRoute = [
    "/",
    "/security",
    "/personalization",
    SUBSCRIPTIONS_PATH,
    PAYMENTS_PATH,
    GIFTS_PATH,
  ].includes(pathname);

  const isCustomPage = pathname.startsWith("/custom-page/");

  const showNavigationMenu = isPrimaryRoute || isCustomPage;
  const { apiCall: getGlobalCss, response: globalCss } = useFetch("get");

  useEffect(() => {
    getGlobalCss(templatesApiUrl("global", isPreviewMode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redirectToScreen = (
    path: string,
    redirectPath: string,
    message: string,
    success: boolean
  ) => {
    if (pathname === path) {
      navigate(redirectPath);
      setTimeout(() => {
        if (success) {
          toast.success(`${t(message)}`);
        } else {
          toast.error(`${t(message)}`);
        }
      }, 1000);
    }
  };

  useEffect(() => {
    redirectToScreen(
      "/LoginAlreadyAssociated",
      "/external-logins",
      "general.socialLoginExists",
      false
    );

    redirectToScreen(
      "/ExternalProviderLinkingError",
      "/external-logins",
      "general.socialLoginDidNotLinked",
      false
    );

    redirectToScreen(
      "/ExternalProviderLinkedSuccessfully",
      "/external-logins",
      "recoverEmail.recoveryEmailConnectedSuccessfully",
      true
    );

    redirectToScreen(
      "/RecoveryEmailRemovalFailed",
      RECOVERY_EMAIL_PATH,
      "recoverEmail.recoverEmailDidNotLinked",
      false
    );

    redirectToScreen(
      "/RecoveryEmailDisconnected",
      RECOVERY_EMAIL_PATH,
      "recoverEmail.recoveryEmailDissconnected",
      true
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    !access_token && setVisible(true);
  }, [access_token]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearAllCookies = () => {
    return document.cookie.split(";").forEach(function (c) {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
  };

  return (
    <>
      <Helmet>
        <style>{globalCss?.css}</style>
      </Helmet>
      <BiskoScript />

      <main className="relative min-h-screen">
        <>
          <Compose
            components={[
              UserProfileProvider,
              OrganizationSettingsProvider,
              TfaProvider
            ]}
          >
            <Header />
            <div
              className={cx([
                showNavigationMenu &&
                  "flex md:flex-row flex-col main-container",
              ])}
            >
              {showNavigationMenu && <Sidebar />}
              <Routes>
                <Route path="*" element={<MainRouter />} />
              </Routes>
            </div>
          </Compose>
        </>
        <ToastHolder />
      </main>

      {/* TODO: should be a component */}
      {visible && (
        <Modal
          visible={visible}
          title={t("sessionExpired.title")}
          infoModal
          modalSize={ModalSize.sm}
          warningModal
          blockOutsideClick
          noCloseButton
        >
          <p className="text-sm text-gray-700 mb-6 sm:mb-8">
            {t<string>("sessionExpired.title")}
          </p>

          <Button
            type="button"
            onClick={() => {
              window.location.reload();
              window.localStorage.clear();
              clearAllCookies();
            }}
            btnStyle={BtnStyle.primary}
            btnSize={BtnSize.nomral}
            className={cx([
              "flex items-center mb-2 ml-auto",
              primaryBtnClasses,
            ])}
          >
            {t<string>("sessionExpired.description")}
          </Button>
        </Modal>
      )}
    </>
  );
};

export default App;
